"use client";

import useWizard from "@/hooks/useWizard";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  useParams,
  usePathname,
  useSearchParams,
  useRouter,
} from "next/navigation";
import React, { useCallback, useEffect } from "react";
import {
  IAssuranceSanteInitialState,
  initWizard,
  reset,
  updateCurrentStep,
  updateCurrentVisibleStep,
  updateData,
} from "./slice";
import StepContainer from "@/app/[locale]/(services)/components/shared/StepContainer";
import Question from "@/app/[locale]/(services)/components/shared/Question/Question";
import QuestionLabel from "@/app/[locale]/(services)/components/shared/Question/QuestionLabel";
import QuestionSingleRadio from "@/app/[locale]/(services)/components/shared/Question/QuestionSingleRadio";
import QuestionAdherent, {
  TypeEnum,
} from "@/app/[locale]/(services)/components/shared/Question/QuestionAdherent";
import QuestionCustomSelect from "@/app/[locale]/(services)/components/shared/Question/QuestionCustomSelect";
import ProcessingLoader from "@/app/[locale]/(services)/components/shared/ProcessingLoader";
import { HiChevronLeft } from "react-icons/hi2";
import moment from "moment";
import SuccessRequestPage from "@/app/[locale]/(services)/components/shared/SuccessRequestPage";
import QuestionInput from "@/app/[locale]/(services)/components/shared/Question/QuestionTextInput";
import PhoneInputWithCountrySelect, {
  isValidPhoneNumber,
} from "react-phone-number-input";
import { usePostAssuranceRequestMutation } from "@/lib/services/v1";
import { toast } from "react-toastify";
import QuestionPackage from "@/app/[locale]/(services)/components/shared/Question/QuestionPackage";
import { TwoValueTuple } from "@/lib/types";
import { useTranslation } from "react-i18next";
import ContactInformation from "@/app/[locale]/(services)/components/shared/ContactInformation";
import QuestionAddressInput from "@/app/[locale]/(services)/components/shared/Question/QuestionAddressInput";

const AssuranceSante = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const params = useParams();
  const searchParams = useSearchParams();
  const path = usePathname();
  const router = useRouter();
  const wizard = useWizard();

  const [onFinish, onFinishResults] = usePostAssuranceRequestMutation();

  const state = useAppSelector((state) => state.services.assuranceSante);

  const handleFinish = () => {
    onFinish({ service: "Assurance Sante", data: state.data })
      .unwrap()
      .then((res) => {
        dispatch(
          updateCurrentVisibleStep({
            id: "summary",
            group: "summary",
            newTab: true,
          })
        );

        if (state.currentStep.group === "my_contact" && !wizard.isLastStep)
          wizard.nextStep();
      })
      .catch((err) => {
        // TODO => If it has validation error manage it's state otherwise show toast

        toast.error(err.response.data.message || err.message);
      });
  };

  const onSelectAddress = (address:any) => {
    dispatch(
      updateData({
        data: {
          item: "npa",
          value: address.full_address || "",
        },
        currentStep: { id: "assurance-actuelle" },
        currentVisibleStep: { id: "assurance-actuelle" },
      }),
    );

    if (state.currentStep.group === "adherent") {
      wizard.nextStep();
    }
  };

  const franchiseOptions = [
    { label: "300 CHF", value: "300" },
    { label: "500 CHF", value: "500" },
    { label: "1000 CHF", value: "1000" },
    { label: "1500 CHF", value: "1500" },
    { label: "2000 CHF", value: "2000" },
    { label: "2500 CHF", value: "2500" },
  ];

  const franchiseOptionsForChilds = [
    { label: "0 CHF", value: "0" },
    { label: "100 CHF", value: "100" },
    { label: "200 CHF", value: "200" },
    { label: "300 CHF", value: "300" },
    { label: "400 CHF", value: "400" },
    { label: "500 CHF", value: "500" },
    { label: "600 CHF", value: "600" },
  ];

  const packageOptions = {
    options: [
      {
        id: 1,
        principal: "Essentials",
        features: [
          { name: t("AssuranceSante.package.key2"), isIncluded: true },
          { name: t("AssuranceSante.package.key3"), isIncluded: true },
          { name: t("AssuranceSante.package.key4"), isIncluded: true },
          { name: t("AssuranceSante.package.key5"), isIncluded: true },
          { name: t("AssuranceSante.package.key6"), isIncluded: true },
          { name: t("AssuranceSante.package.key7"), isIncluded: false },
          { name: t("AssuranceSante.package.key8"), isIncluded: false },
          { name: t("AssuranceSante.package.key9"), isIncluded: false },
          { name: t("AssuranceSante.package.key10"), isIncluded: false },
          { name: t("AssuranceSante.package.key11"), isIncluded: false },
        ],
      },
      {
        id: 2,
        principal: "Comfort",
        features: [
          { name: t("AssuranceSante.package.key2"), isIncluded: true },
          { name: t("AssuranceSante.package.key3"), isIncluded: true },
          { name: t("AssuranceSante.package.key4"), isIncluded: true },
          { name: t("AssuranceSante.package.key5"), isIncluded: true },
          { name: t("AssuranceSante.package.key6"), isIncluded: true },
          { name: t("AssuranceSante.package.key7"), isIncluded: true },
          { name: t("AssuranceSante.package.key8"), isIncluded: true },
          { name: t("AssuranceSante.package.key9"), isIncluded: false },
          { name: t("AssuranceSante.package.key10"), isIncluded: false },
          { name: t("AssuranceSante.package.key11"), isIncluded: false },
        ],
        isRecommendedPackage: true,
      },
      {
        id: 3,
        principal: "Premium",
        features: [
          { name: t("AssuranceSante.package.key2"), isIncluded: true },
          { name: t("AssuranceSante.package.key3"), isIncluded: true },
          { name: t("AssuranceSante.package.key4"), isIncluded: true },
          { name: t("AssuranceSante.package.key5"), isIncluded: true },
          { name: t("AssuranceSante.package.key6"), isIncluded: true },
          { name: t("AssuranceSante.package.key7"), isIncluded: true },
          { name: t("AssuranceSante.package.key8"), isIncluded: true },
          { name: t("AssuranceSante.package.key9"), isIncluded: true },
          { name: t("AssuranceSante.package.key10"), isIncluded: true },
          { name: t("AssuranceSante.package.key11"), isIncluded: true },
        ],
      },
    ],
    additionalOptions: [
      {
        id: 1,
        label: t("AssuranceSante.package.key12"),
        value: "Médecine alternative",
        isRecommended: false,
        features: [
          t("AssuranceSante.package.key13"),
          t("AssuranceSante.package.key14"),
        ],
      },
      {
        id: 2,
        label: t("AssuranceSante.package.key15"),
        value: "Traitements dentaires",
        levelRange: [1, 3] as TwoValueTuple<number, number>,
        isRecommended: false,
        features: {
          1: {
            label: t("AssuranceSante.package.key16", { level: "1" }),
            features: [
              t("AssuranceSante.package.key17"),
              t("AssuranceSante.package.key18"),
            ],
          },
          2: {
            label: t("AssuranceSante.package.key16", { level: "2" }),
            features: [
              t("AssuranceSante.package.key19"),
              t("AssuranceSante.package.key20"),
            ],
          },
          3: {
            label: t("AssuranceSante.package.key16", { level: "3" }),
            features: [
              t("AssuranceSante.package.key21"),
              t("AssuranceSante.package.key22"),
            ],
          },
        },
      },
      {
        id: 3,
        label: t("AssuranceSante.package.key23"),
        value: "Capital hospitalier",
        levelRange: [1, 3] as TwoValueTuple<number, number>,
        isRecommended: false,
        features: {
          1: {
            label: t("AssuranceSante.package.key16", { level: "1" }),
            features: [t("AssuranceSante.package.key24")],
          },
          2: {
            label: t("AssuranceSante.package.key16", { level: "2" }),
            features: [t("AssuranceSante.package.key25")],
          },
          3: {
            label: t("AssuranceSante.package.key16", { level: "3" }),
            features: [t("AssuranceSante.package.key26")],
          },
        },
      },
      {
        id: 4,
        label: t("AssuranceSante.package.key27"),
        value: "Hospitalisation",
        levelRange: [1, 3] as TwoValueTuple<number, number>,
        isRecommended: true,
        features: {
          1: {
            label: t("AssuranceSante.package.key16", { level: "Flexible" }),
            features: [t("AssuranceSante.package.key28")],
          },
          2: {
            label: t("AssuranceSante.package.key16", { level: "Semi-privée" }),
            features: [
              t("AssuranceSante.package.key29"),
              t("AssuranceSante.package.key30"),
            ],
          },
          3: {
            label: t("AssuranceSante.package.key16", { level: "Privée" }),
            features: [
              t("AssuranceSante.package.key31"),
              t("AssuranceSante.package.key32"),
              t("AssuranceSante.package.key33"),
            ],
          },
        },
      },
    ],
  };

  const defaultPack = {
    id: 2,
    principal: "Confort",
    options: [
      {
        id: 3,
        label: t("AssuranceSante.package.key34"),
        level: 2,
      },
      {
        id: 4,
        label: t("AssuranceSante.package.key35"),
        level: 1,
      },
    ],
  };

  const isOlderThan = (birthdate: string, targetAge: number) => {
    const now = moment();
    const birthDateMoment = moment(birthdate);
    const age = now.diff(birthDateMoment, "years");
    return age >= targetAge;
  };

  const canConfirm = useCallback(() => {
    switch (state.data.for) {
      case "you":
        return Object.hasOwn(
          state.data.adherent.find((adhr) => adhr.type === "main") as object,
          "couverture"
        );
      case "you and your partner":
        return (
          Object.hasOwn(
            state.data.adherent.find((adhr) => adhr.type === "main") as object,
            "couverture"
          ) &&
          Object.hasOwn(
            state.data.adherent.find(
              (adhr) => adhr.type === "partner"
            ) as object,
            "travailSuisse"
          ) &&
          Object.hasOwn(
            state.data.adherent.find(
              (adhr) => adhr.type === "partner"
            ) as object,
            "couverture"
          )
        );
      case "you and your kids":
        return (
          Object.hasOwn(
            state.data.adherent.find((adhr) => adhr.type === "main") as object,
            "couverture"
          ) &&
          state.data.adherent
            .filter(
              (adhr) => adhr.type === "children" && isOlderThan(adhr.dob, 18)
            )
            .every((item) => Object.hasOwn(item, "travailSuisse"))
        );
      case "you, your partner and your kids":
        return (
          Object.hasOwn(
            state.data.adherent.find((adhr) => adhr.type === "main") as object,
            "couverture"
          ) &&
          Object.hasOwn(
            state.data.adherent.find(
              (adhr) => adhr.type === "partner"
            ) as object,
            "travailSuisse"
          ) &&
          Object.hasOwn(
            state.data.adherent.find(
              (adhr) => adhr.type === "partner"
            ) as object,
            "couverture"
          ) &&
          state.data.adherent
            .filter(
              (adhr) => adhr.type === "children" && isOlderThan(adhr.dob, 18)
            )
            .every((item) => Object.hasOwn(item, "travailSuisse"))
        );
      default:
        return false;
    }
  }, [state.data.for, state.data.adherent]);

  const checkRequiredFields = () => {
    const mainAdherent = state.data.adherent.find(
      (adhr) => adhr.type === "main"
    );
    const partnerAdherent = state.data.adherent.find(
      (adhr) => adhr.type === "partner"
    );
    const childrenAdherents = state.data.adherent.filter(
      (adhr) => adhr.type === "children"
    );

    // For single person
    if (state.data.for === "you") {
      if (!mainAdherent?.couvertureAccident) {
        return false;
      }
    }

    // For couple
    if (state.data.for === "you and your partner") {
      if (
        !mainAdherent?.couvertureAccident ||
        !partnerAdherent?.couvertureAccident
      ) {
        return false;
      }
    }

    // Check children franchise if any exist
    if (childrenAdherents.length > 0) {
      const allChildrenHaveFranchise = childrenAdherents.every(
        (child) => child.franchise
      );
      if (!allChildrenHaveFranchise) {
        return false;
      }
    }

    return true;
  };

  // Effect which initialize the wizard on first load
  useEffect(() => {
    if (!state.versionId) {
      dispatch(initWizard());
    }
  }, [dispatch, state.versionId]);

  // Effect which updates url with actual visible step
  useEffect(() => {
    router.push(`${path}?step=${state.currentVisibleStep.id}`);

    return () => {};
  }, [path, router, state.currentVisibleStep.id]);

  // Effect which scrolls to current url step
  useEffect(() => {
    if (searchParams.get("step")) {
      wizard.goTo(searchParams.get("step")!);
    }
  }, [searchParams, wizard]);

  let content;

  if (state.currentVisibleStep.id === "loader")
    content = (
      <ProcessingLoader
        callBack={() => {
          dispatch(
            updateCurrentVisibleStep({
              id: "info",
              group: "my_contact",
              newTab: true,
            })
          );
        }}
      />
    );
  else if (state.currentVisibleStep.group === "summary")
    content = (
      <SuccessRequestPage
        callBack={() => {
          dispatch(reset());

          wizard.reset();

          router.push("/");
        }}
        type={""}
      />
    );
  else if (state.currentVisibleStep.group === "my_contact")
    content = (
      <ContactInformation
        backHandler={() => {
          dispatch(
            updateCurrentVisibleStep({
              id:
                state.data.situation === "frontalier"
                  ? "work-hours"
                  : "franchise",
              group:
                state.data.situation === "frontalier" ? "adherent" : "besoins",
            })
          );

          dispatch(
            updateCurrentStep({
              id:
                state.data.situation === "frontalier"
                  ? "work-hours"
                  : "franchise",
              group:
                state.data.situation === "frontalier" ? "adherent" : "besoins",
              branch:
                state.data.situation === "frontalier" ? "standard" : "suisse",
            })
          );
        }}
        hasAlert
        state={state}
        updator={(data: any) => dispatch(updateData(data))}
        handleFinish={handleFinish}
        isSubmitting={onFinishResults.isLoading}
      />
    );
  else if (state.currentVisibleStep.group === "finalisation")
    content = (
      <React.Fragment>
        <Question id="economies">
          <button
            className="mb-8 flex h-11 w-fit items-center gap-2.5 rounded-full border border-[#88889440] bg-[#8888941A] px-5 text-base font-semibold text-[#082623CC] transition-colors hover:bg-[#082623CC] hover:text-white md:mb-[40px]"
            onClick={() =>
              dispatch(
                updateCurrentVisibleStep({
                  id: "package",
                  newTab: false,
                  group: "besoins",
                })
              )
            }
          >
            <HiChevronLeft />
            {t("AssuranceSante.economies.key1")}
          </button>

          <Question>
            <QuestionLabel
              title={t("AssuranceSante.economies.key2")}
              description={t("AssuranceSante.economies.key3")}
            />
            <QuestionSingleRadio
              value={state.data.economieimpots}
              options={[
                {
                  label: t("AssuranceSante.economies.key4"),
                  value: "yes",
                },
                {
                  label: t("AssuranceSante.economies.key5"),
                  value: "no",
                },
              ]}
              onSelectOption={(value) => {
                dispatch(
                  updateData({
                    data: {
                      item: "economieimpots",
                      value: value,
                    },
                    currentStep: { id: "info", group: "my_contact" },
                    currentVisibleStep: { id: "loader", group: "my_contact" },
                  })
                );

                if (state.currentStep.group === "finalisation")
                  wizard.nextStep();
              }}
            />
          </Question>
        </Question>
      </React.Fragment>
    );
  else if (state.currentVisibleStep.group === "besoins")
    content = (
      <React.Fragment>
        <Question id="franchise">
          <button
            className="mb-8 flex h-11 w-fit items-center gap-2.5 rounded-full border border-[#88889440] bg-[#8888941A] px-5 text-base font-semibold text-[#082623CC] transition-colors hover:bg-[#082623CC] hover:text-white md:mb-[40px]"
            onClick={() =>
              dispatch(
                updateCurrentVisibleStep({
                  id:
                    state.data.npa === "Not on the territory"
                      ? "situation"
                      : "assurance-actuelle",
                  newTab: false,
                  group: "adherent",
                })
              )
            }
          >
            <HiChevronLeft />
            {t("AssuranceSante.franchise.key1")}
          </button>

          <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <Question>
                <QuestionLabel
                  title={t("AssuranceSante.franchise.key2")}
                  description={t("AssuranceSante.franchise.key3")}
                />
                <QuestionCustomSelect
                  placeholder={t("AssuranceSante.franchise.key4")}
                  options={franchiseOptions}
                  value={
                    state.data.adherent.find((adhr) => adhr.type === "main")
                      ?.franchise ?? ""
                  }
                  onSelectValue={(value) => {
                    const clone = state.data.adherent.map((adher) => {
                      if (adher.type === "main")
                        return {
                          ...adher,
                          franchise: value,
                        };
                      else return adher;
                    });

                    dispatch(
                      updateData({
                        data: {
                          item: "adherent",
                          value: clone,
                        },
                      })
                    );
                  }}
                />
              </Question>

              {Object.hasOwn(
                state.data.adherent.find((adhr) => adhr.type === "main")!,
                "franchise"
              ) && (
                <Question>
                  <QuestionLabel
                    title={t("AssuranceSante.franchise.key5")}
                    description={t("AssuranceSante.franchise.key6")}
                  />
                  <QuestionSingleRadio
                    inline
                    value={
                      state.data.adherent.find((adhr) => adhr.type === "main")
                        ?.couvertureAccident ?? ""
                    }
                    options={[
                      {
                        label: t("AssuranceSante.franchise.key7"),
                        value: "yes",
                      },
                      {
                        label: t("AssuranceSante.franchise.key8"),
                        value: "no",
                      },
                    ]}
                    onSelectOption={(value) => {
                      const clone = state.data.adherent.map((adher) => {
                        if (adher.type === "main")
                          return {
                            ...adher,
                            couvertureAccident: value,
                            pack: defaultPack,
                          };
                        else return adher;
                      });

                      dispatch(
                        updateData({
                          data: {
                            item: "adherent",
                            value: clone,
                          },
                          currentStep: { id: "package" },
                          currentVisibleStep: { id: "package" },
                        })
                      );
                    }}
                  />
                </Question>
              )}
            </div>

            {(state.data.for === "you and your partner" ||
              state.data.for === "you, your partner and your kids") && (
              <div className="flex flex-col gap-4">
                <Question>
                  <QuestionLabel
                    title={t("AssuranceSante.franchise.key9")}
                    description={t("AssuranceSante.franchise.key10")}
                  />
                  <QuestionCustomSelect
                    placeholder={t("AssuranceSante.franchise.key4")}
                    options={franchiseOptions}
                    value={
                      state.data.adherent.find(
                        (adhr) => adhr.type === "partner"
                      )?.franchise ?? ""
                    }
                    onSelectValue={(value) => {
                      const clone = state.data.adherent.map((adher) => {
                        if (adher.type === "partner")
                          return {
                            ...adher,
                            franchise: value,
                          };
                        else return adher;
                      });

                      dispatch(
                        updateData({
                          data: {
                            item: "adherent",
                            value: clone,
                          },
                        })
                      );
                    }}
                  />
                </Question>

                {Object.hasOwn(
                  state.data.adherent.find((adhr) => adhr.type === "partner")!,
                  "franchise"
                ) && (
                  <Question>
                    <QuestionLabel
                      title={t("AssuranceSante.franchise.key11")}
                      description={t("AssuranceSante.franchise.key12")}
                    />
                    <QuestionSingleRadio
                      inline
                      value={
                        state.data.adherent.find(
                          (adhr) => adhr.type === "partner"
                        )?.couvertureAccident ?? ""
                      }
                      options={[
                        {
                          label: t("AssuranceSante.franchise.key7"),
                          value: "yes",
                        },
                        {
                          label: t("AssuranceSante.franchise.key8"),
                          value: "no",
                        },
                      ]}
                      onSelectOption={(value) => {
                        const clone = state.data.adherent.map((adher) => {
                          if (adher.type === "partner")
                            return {
                              ...adher,
                              couvertureAccident: value,
                            };
                          else return adher;
                        });

                        dispatch(
                          updateData({
                            data: {
                              item: "adherent",
                              value: clone,
                            },
                            currentStep: { id: "package" },
                            currentVisibleStep: { id: "package" },
                          })
                        );
                      }}
                    />
                  </Question>
                )}
              </div>
            )}

            {state.data.adherent
              .filter((adhr) => adhr.type === "children")
              .map((child, index) => (
                <Question key={index}>
                  <QuestionLabel
                    title={t("AssuranceSante.franchise.key13")}
                    description={
                      !isOlderThan(child.year, 15)
                        ? t("AssuranceSante.franchise.key14")
                        : t("AssuranceSante.franchise.key15")
                    }
                  />
                  <QuestionCustomSelect
                    placeholder={t("AssuranceSante.franchise.key4")}
                    options={
                      !isOlderThan(child.year, 15)
                        ? franchiseOptionsForChilds
                        : franchiseOptions
                    }
                    value={child.franchise ?? ""}
                    onSelectValue={(value) => {
                      const clone = state.data.adherent.map((adher) => {
                        if (JSON.stringify(adher) === JSON.stringify(child))
                          return {
                            ...adher,
                            franchise: value,
                          };
                        else return adher;
                      });

                      dispatch(
                        updateData({
                          data: {
                            item: "adherent",
                            value: clone,
                          },
                        })
                      );
                    }}
                  />
                </Question>
              ))}
          </div>
        </Question>

        {checkRequiredFields() && (
          <React.Fragment>
            <Question id="package">
              <div className="flex flex-col gap-8">
                {/* QuestionPackage for main */}
                <Question>
                  <div className="flex items-start justify-between gap-[10px]">
                    <div className="mb-[20px] flex flex-col gap-[10px]">
                      {t("AssuranceSante.package.key1")}
                    </div>
                  </div>
                  <QuestionPackage
                    value={
                      state.data.adherent.find((adhr) => adhr.type === "main")
                        ?.pack || null
                    }
                    adhrType="main"
                    options={packageOptions.options}
                    additionalOptions={packageOptions.additionalOptions}
                    confirmButtonText={t("AssuranceSante.package.key36")}
                    resetButton={{ text: t("AssuranceSante.package.key37") }}
                    onReset={() => {
                      const clone = state.data.adherent.map((adher) => {
                        if (adher.type === "main")
                          return {
                            ...adher,
                            pack: undefined,
                          };
                        else return adher;
                      });

                      dispatch(
                        updateData({
                          data: {
                            item: "adherent",
                            value: clone,
                          },
                        })
                      );
                    }}
                    onSubmit={() => {
                      const hasPartner = state.data.adherent.some(
                        (adhr) => adhr.type === "partner"
                      );

                      const hasChildrens = state.data.adherent.some(
                        (adhr) => adhr.type === "children"
                      );

                      if (!hasPartner && !hasChildrens) {
                        dispatch(
                          updateCurrentVisibleStep({
                            id: "economies",
                            group: "finalisation",
                            newTab: true,
                            branch: "suisse",
                          })
                        );

                        dispatch(
                          updateCurrentStep({
                            id: "economies",
                            group: "finalisation",
                            newTab: true,
                            branch: "suisse",
                          })
                        );

                        if (state.currentStep.group === "besoins")
                          wizard.nextStep();
                      }

                      if (hasPartner) {
                        // Initialize partner pack with same value as main adherent
                        const clone = state.data.adherent.map((adher) => {
                          if (adher.type === "partner") {
                            return {
                              ...adher,
                              pack: defaultPack,
                            };
                          }
                          return adher;
                        });

                        dispatch(
                          updateData({
                            data: {
                              item: "adherent",
                              value: clone,
                            },
                          })
                        );
                      }

                      if (!hasPartner && hasChildrens) {
                        // Initialize children pack with same value as main adherent
                        const clone = state.data.adherent.map((adher) => {
                          if (adher.type === "children") {
                            return {
                              ...adher,
                              pack: defaultPack,
                            };
                          }
                          return adher;
                        });

                        dispatch(
                          updateData({
                            data: {
                              item: "adherent",
                              value: clone,
                            },
                          })
                        );
                      }
                    }}
                    isSubmitAccessible={
                      state.data.adherent.find((adhr) => adhr.type === "main")
                        ?.pack !== undefined &&
                      (!state.data.adherent.find(
                        (adhr) => adhr.type === "partner"
                      ) ||
                        state.data.adherent.find(
                          (adhr) => adhr.type === "partner"
                        )?.pack === undefined) &&
                      (!state.data.adherent.some(
                        (item) => item.type === "children"
                      ) ||
                        state.data.adherent
                          .filter((item) => item.type === "children")
                          .every((child) => child.pack === undefined))
                    }
                  />
                </Question>
                {/* QuestionPackage for partner */}
                {state.data.adherent.find(
                  (adhr) => adhr.type === "partner" 
                  // && adhr.pack
                ) && (
                  <Question>
                    <div className="flex flex-col gap-8">
                      <Question>
                        <div className="flex items-start justify-between gap-[10px]">
                          <div className="mb-[20px] flex flex-col gap-[10px]">
                            {state.data.adherent.find(
                              (adhr) => adhr.type === "partner"
                            )?.civility === "male"
                              ? t("AssuranceSante.package.key38")
                              : t("AssuranceSante.package.key39")}
                          </div>
                        </div>
                        <QuestionPackage
                          value={
                            state.data.adherent.find(
                              (adhr) => adhr.type === "partner"
                            )?.pack || null
                          }
                          adhrType="partner"
                          options={packageOptions.options}
                          additionalOptions={packageOptions.additionalOptions}
                          confirmButtonText={t("AssuranceSante.package.key36")}
                          resetButton={{
                            text: t("AssuranceSante.package.key37"),
                          }}
                          onReset={() => {
                            const clone = state.data.adherent.map((adher) => {
                              if (adher.type === "partner")
                                return {
                                  ...adher,
                                  pack: undefined,
                                };
                              else return adher;
                            });

                            dispatch(
                              updateData({
                                data: {
                                  item: "adherent",
                                  value: clone,
                                },
                              })
                            );
                          }}
                          onSubmit={() => {
                            const hasChildrens = state.data.adherent.some(
                              (adhr) => adhr.type === "children"
                            );

                            if (hasChildrens) {
                              // Initialize children pack with same value as main adherent
                              const clone = state.data.adherent.map((adher) => {
                                if (adher.type === "children") {
                                  return {
                                    ...adher,
                                    pack: defaultPack,
                                  };
                                }
                                return adher;
                              });

                              dispatch(
                                updateData({
                                  data: {
                                    item: "adherent",
                                    value: clone,
                                  },
                                })
                              );
                            } else {
                              dispatch(
                                updateCurrentVisibleStep({
                                  id: "economies",
                                  group: "finalisation",
                                  newTab: true,
                                  branch: "suisse",
                                })
                              );

                              dispatch(
                                updateCurrentStep({
                                  id: "economies",
                                  group: "finalisation",
                                  newTab: true,
                                  branch: "suisse",
                                })
                              );

                              if (state.currentStep.group === "besoins")
                                wizard.nextStep();
                            }
                          }}
                          isSubmitAccessible={
                            state.data.adherent.find(
                              (adhr) => adhr.type === "partner"
                            )?.pack !== undefined &&
                            (!state.data.adherent.some(
                              (item) => item.type === "children"
                            ) ||
                              state.data.adherent
                                .filter((item) => item.type === "children")
                                .every((child) => child.pack === undefined))
                          }
                        />
                      </Question>
                    </div>
                  </Question>
                )}
                {/* QuestionPackage for each children */}
                {((state.data.for === TypeEnum.SingleWithChild &&
                  state.data.adherent.find((adhr) => adhr.type === "main")
                    ?.pack !== undefined) ||
                  (state.data.for === TypeEnum.CoupleWithChild &&
                    state.data.adherent.find((adhr) => adhr.type === "partner")
                      ?.pack !== undefined)) &&
                  state.data.adherent
                    .filter(
                      (adhr) => adhr.type === "children"
                      // && adhr.pack
                    )
                    .map((child, index) => (
                      <Question key={index}>
                        <div className="flex flex-col gap-8">
                          <Question>
                            <div className="flex items-start justify-between gap-[10px]">
                              <div className="mb-[20px] flex flex-col gap-[10px]">
                                {t("AssuranceSante.package.key40", {
                                  year: child.year,
                                })}
                              </div>
                            </div>
                            <QuestionPackage
                              value={child.pack || null}
                              adhrType="children"
                              options={packageOptions.options}
                              additionalOptions={
                                packageOptions.additionalOptions
                              }
                              confirmButtonText={t(
                                "AssuranceSante.package.key36"
                              )}
                              resetButton={{
                                text: t("AssuranceSante.package.key37"),
                              }}
                              onReset={() => {
                                const clone = state.data.adherent.map(
                                  (adher) => {
                                    if (
                                      JSON.stringify(adher) ===
                                      JSON.stringify(child)
                                    )
                                      return {
                                        ...adher,
                                        pack: undefined,
                                      };
                                    else return adher;
                                  }
                                );

                                dispatch(
                                  updateData({
                                    data: {
                                      item: "adherent",
                                      value: clone,
                                    },
                                  })
                                );
                              }}
                              onSubmit={() => {
                                dispatch(
                                  updateCurrentVisibleStep({
                                    id: "economies",
                                    group: "finalisation",
                                    newTab: true,
                                    branch: "suisse",
                                  })
                                );

                                dispatch(
                                  updateCurrentStep({
                                    id: "economies",
                                    group: "finalisation",
                                    newTab: true,
                                    branch: "suisse",
                                  })
                                );

                                if (state.currentStep.group === "besoins")
                                  wizard.nextStep();
                              }}
                              isSubmitAccessible={
                                state.data.adherent.find(
                                  (adhr) =>
                                    JSON.stringify(adhr) ===
                                    JSON.stringify(child)
                                )?.pack !== undefined &&
                                (!state.data.adherent.some(
                                  (item) => item.type === "partner"
                                ) ||
                                  state.data.adherent.find(
                                    (item) => item.type === "partner"
                                  )?.pack === undefined ||
                                  child ===
                                    state.data.adherent[
                                      state.data.adherent.length - 1
                                    ])
                              }
                            />
                          </Question>
                        </div>
                      </Question>
                    ))}
              </div>
            </Question>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  else
    content = (
      <React.Fragment>
        <Question id="for-who">
          <h1 className="text-[#f9fafd]">{t("AssuranceSante.for-who.key1")}</h1>
          <QuestionLabel title={t("AssuranceSante.for-who.key1")} />
          <QuestionSingleRadio
            value={state.data.for}
            options={[
              {
                label: t("AssuranceSante.for-who.key2"),
                value: "you",
                appendIcon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="38"
                    height="38"
                    fill="none"
                    viewBox="0 0 38 38"
                  >
                    <path
                      fill="#404040"
                      d="M22.93 32.615V22.228a3.019 3.019 0 0 0 2.813-3.007v-4.58a5.016 5.016 0 0 0-4.371-4.964 3.679 3.679 0 1 0-4.744 0 5.016 5.016 0 0 0-4.37 4.964v4.58a3.019 3.019 0 0 0 2.811 3.008v10.386A2.192 2.192 0 0 0 19 33.953a2.193 2.193 0 0 0 3.93-1.338ZM16.235 6.87a2.767 2.767 0 1 1 5.534 0 2.767 2.767 0 0 1-5.534 0Zm3.222 25.746v-8.75a.456.456 0 1 0-.912 0v8.75a1.281 1.281 0 0 1-2.563 0V14.578a.456.456 0 1 0-.912 0v6.736a2.106 2.106 0 0 1-1.9-2.093v-4.58a4.098 4.098 0 0 1 4.094-4.094h3.474a4.098 4.098 0 0 1 4.094 4.094v4.58a2.106 2.106 0 0 1-1.9 2.093v-6.736a.456.456 0 1 0-.912 0v18.037a1.281 1.281 0 0 1-2.563 0Z"
                    ></path>
                  </svg>
                ),
              },
              {
                label: t("AssuranceSante.for-who.key3"),
                value: "you and your partner",
                appendIcon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="38"
                    height="38"
                    fill="none"
                    viewBox="0 0 38 38"
                  >
                    <path
                      fill="#404040"
                      d="M14.413 9.677a3.678 3.678 0 1 0-4.745 0 5.012 5.012 0 0 0-4.37 4.964v4.58A3.018 3.018 0 0 0 8.11 22.23v10.386a2.193 2.193 0 0 0 3.931 1.338 2.193 2.193 0 0 0 3.931-1.338V22.228a3.018 3.018 0 0 0 2.812-3.007v-4.58a5.011 5.011 0 0 0-4.371-4.964ZM9.272 6.87a2.766 2.766 0 1 1 5.533 0 2.766 2.766 0 0 1-5.533 0Zm8.599 12.352a2.105 2.105 0 0 1-1.9 2.093v-6.736a.456.456 0 1 0-.912 0v18.037a1.281 1.281 0 0 1-2.563 0v-8.751a.456.456 0 1 0-.912 0v8.751a1.282 1.282 0 0 1-2.564 0V14.577a.456.456 0 0 0-.912 0v6.737a2.106 2.106 0 0 1-1.9-2.093v-4.58a4.098 4.098 0 0 1 4.093-4.094h3.476a4.098 4.098 0 0 1 4.092 4.094l.002 4.58Zm10.461-7.763a3.678 3.678 0 1 0-4.745 0 5.011 5.011 0 0 0-4.37 4.963v2.8a3.005 3.005 0 0 0 1.874 2.792l.022.007-1.863 4.648a.456.456 0 0 0 .423.626h2.356v5.32a2.193 2.193 0 0 0 3.931 1.338 2.193 2.193 0 0 0 3.931-1.338v-5.32h2.357a.456.456 0 0 0 .423-.626l-1.865-4.65a.536.536 0 0 0 .057-.02 3.012 3.012 0 0 0 1.84-2.777v-2.8a5.011 5.011 0 0 0-4.371-4.963ZM23.194 8.65a2.766 2.766 0 1 1 5.532-.001 2.766 2.766 0 0 1-5.532.001Zm1.028 25.246a1.283 1.283 0 0 1-1.281-1.281v-5.32h2.562v5.32a1.283 1.283 0 0 1-1.281 1.281Zm4.757-1.281a1.282 1.282 0 0 1-2.564 0v-5.32h2.564v5.32ZM31.79 19.22a2.1 2.1 0 0 1-1.285 1.938l-.038.019-1.069-2.665v-3.22a.456.456 0 0 0-.912 0v3.309c0 .058.011.116.033.17l3.053 7.61H20.347l3.053-7.609a.464.464 0 0 0 .033-.17v-3.31a.456.456 0 0 0-.912 0v3.22l-1.069 2.66-.016-.007a2.096 2.096 0 0 1-1.307-1.947v-2.797a4.098 4.098 0 0 1 4.093-4.093h3.475a4.098 4.098 0 0 1 4.093 4.093v2.8Z"
                    ></path>
                  </svg>
                ),
              },
              {
                label: t("AssuranceSante.for-who.key4"),
                value: "you and your kids",
                appendIcon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="38"
                    height="38"
                    fill="none"
                    viewBox="0 0 38 38"
                  >
                    <path
                      fill="#404040"
                      d="M7.301 32.615a2.193 2.193 0 0 0 3.931 1.338 2.193 2.193 0 0 0 3.932-1.338v-5.32h2.356a.455.455 0 0 0 .436-.588l-1.48-4.881a3.009 3.009 0 0 0 1.499-2.605v-4.58a5.011 5.011 0 0 0-4.371-4.964 3.679 3.679 0 1 0-4.745 0 5.012 5.012 0 0 0-4.37 4.964v4.58a3.001 3.001 0 0 0 1.498 2.605l-1.479 4.88a.456.456 0 0 0 .437.588h2.356v5.321Zm2.194 1.281a1.282 1.282 0 0 1-1.282-1.281v-5.32h2.563v5.32a1.282 1.282 0 0 1-1.281 1.281Zm4.757-1.281a1.282 1.282 0 0 1-2.564 0v-5.32h2.563v5.32ZM8.466 6.869a2.766 2.766 0 1 1 5.532 0 2.766 2.766 0 0 1-5.532 0Zm.22 9.196a.456.456 0 0 0 .02-.132v-2.42a.456.456 0 1 0-.912 0v2.355l-1.53 5.05a2.094 2.094 0 0 1-.862-1.697v-4.58a4.098 4.098 0 0 1 4.093-4.094h3.474a4.098 4.098 0 0 1 4.094 4.094v4.58a2.096 2.096 0 0 1-.861 1.697l-1.534-5.05v-2.356a.456.456 0 0 0-.912 0v2.424c0 .044.007.09.02.132L16.9 26.382H5.56l3.125-10.317Zm24.662 2.117a1.7 1.7 0 0 0-.966-.882 1.712 1.712 0 0 0-2.19 1.027 4.53 4.53 0 0 1-1.361 1.938 3.127 3.127 0 1 0-5.822 0 4.53 4.53 0 0 1-1.362-1.938 1.71 1.71 0 0 0-3.216 1.163 7.994 7.994 0 0 0 4.153 4.516v8.907a1.896 1.896 0 0 0 3.332 1.232 1.895 1.895 0 0 0 3.336-1.232V24.01a7.993 7.993 0 0 0 4.153-4.516 1.7 1.7 0 0 0-.057-1.308v-.003Zm-9.644.947a2.214 2.214 0 1 1 2.597 2.18 4.556 4.556 0 0 1-.76 0 2.218 2.218 0 0 1-1.837-2.18Zm8.846.053a7.075 7.075 0 0 1-3.927 4.109.456.456 0 0 0-.28.421v9.201a.984.984 0 0 1-1.968 0v-4.75a.456.456 0 0 0-.912 0v4.75a.984.984 0 0 1-1.968 0v-9.201a.456.456 0 0 0-.28-.421 7.078 7.078 0 0 1-3.927-4.109.798.798 0 1 1 1.5-.542 5.472 5.472 0 0 0 4.623 3.572c.336.055.68.055 1.016 0a5.472 5.472 0 0 0 4.623-3.572.798.798 0 0 1 1.5.542Z"
                    ></path>
                  </svg>
                ),
              },
              {
                label: t("AssuranceSante.for-who.key5"),
                value: "you, your partner and your kids",
                appendIcon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="38"
                    height="38"
                    fill="none"
                    viewBox="0 0 38 38"
                  >
                    <path
                      fill="#404040"
                      d="M27.734 11.458a3.68 3.68 0 1 0-4.745 0 5.017 5.017 0 0 0-3.668 2.41 5.016 5.016 0 0 0-4.31-4.19 3.678 3.678 0 1 0-4.745 0 5.012 5.012 0 0 0-4.37 4.963v4.58a3.018 3.018 0 0 0 2.812 3.008v10.386a2.193 2.193 0 0 0 3.932 1.338 2.19 2.19 0 0 0 3.396.095 1.895 1.895 0 0 0 2.956.096 1.895 1.895 0 0 0 2.964-.108 2.192 2.192 0 0 0 3.406-.084 2.193 2.193 0 0 0 3.931-1.338v-5.32h2.356a.457.457 0 0 0 .423-.626l-1.865-4.65a.464.464 0 0 0 .056-.02 3.011 3.011 0 0 0 1.842-2.777v-2.8a5.011 5.011 0 0 0-4.371-4.963Zm-5.14-2.808a2.767 2.767 0 1 1 5.534 0 2.767 2.767 0 0 1-5.533 0Zm-3.22 12.66a4.543 4.543 0 0 1-.76 0 2.215 2.215 0 1 1 .765 0h-.006Zm-9.5-14.44a2.766 2.766 0 1 1 5.533.001 2.766 2.766 0 0 1-5.535-.002h.001Zm4.502 27.026a1.283 1.283 0 0 1-1.281-1.281v-8.751a.456.456 0 1 0-.912 0v8.751a1.282 1.282 0 0 1-2.564 0V14.577a.456.456 0 0 0-.912 0v6.737a2.106 2.106 0 0 1-1.9-2.093v-4.58a4.098 4.098 0 0 1 4.094-4.094h3.474a4.098 4.098 0 0 1 4.093 4.094v1.406a3.129 3.129 0 0 0-1.9 1.105v-2.575a.456.456 0 1 0-.912 0v5.29a4.515 4.515 0 0 1-.94-1.537 1.71 1.71 0 0 0-3.216 1.163 7.993 7.993 0 0 0 4.156 4.516v8.671a1.283 1.283 0 0 1-1.28 1.216Zm7.038-.983a.984.984 0 0 1-1.968 0v-4.75a.456.456 0 1 0-.912 0v4.75a.984.984 0 0 1-1.967 0v-9.201a.456.456 0 0 0-.281-.421 7.078 7.078 0 0 1-3.927-4.109.798.798 0 1 1 1.5-.542 5.446 5.446 0 0 0 1.865 2.508.459.459 0 0 0 .304.212c.737.478 1.579.77 2.454.853.336.056.68.056 1.017 0a5.438 5.438 0 0 0 1.84-.51.457.457 0 0 0 .338-.177c1.133-.64 2-1.665 2.444-2.888a.796.796 0 0 1 1.3-.335.799.799 0 0 1 .2.878 7.074 7.074 0 0 1-3.927 4.108.457.457 0 0 0-.28.421v9.203Zm2.21.983a1.282 1.282 0 0 1-1.28-1.28v-5.32h2.561v5.32a1.282 1.282 0 0 1-1.281 1.28Zm4.757-1.281a1.282 1.282 0 0 1-2.564 0v-5.32h2.564v5.32Zm2.812-13.395a2.101 2.101 0 0 1-1.324 1.957l-1.065-2.664v-3.22a.456.456 0 1 0-.912 0V18.6c0 .058.011.116.033.17l3.053 7.61h-8.652V24.01a7.994 7.994 0 0 0 4.153-4.516 1.712 1.712 0 0 0-2.333-2.13 1.71 1.71 0 0 0-.882.967 4.5 4.5 0 0 1-.871 1.462l.407-1.02a.448.448 0 0 0 .033-.17v-3.31a.456.456 0 1 0-.912 0v2.753a3.136 3.136 0 0 0-2.375-1.992 4.099 4.099 0 0 1 4.076-3.724h3.475a4.098 4.098 0 0 1 4.093 4.094l.003 2.797Z"
                    ></path>
                  </svg>
                ),
              },
            ]}
            onSelectOption={(value) => {
              dispatch(
                updateData({
                  data: { item: "for", value },
                  currentStep: { id: "adherent" },
                  currentVisibleStep: { id: "adherent" },
                })
              );

              dispatch(
                updateData({
                  data: { item: "adherent", value: [] },
                })
              );

              dispatch(
                updateData({
                  data: { item: "npa", value: "" },
                })
              );
            }}
          />
        </Question>

        {state.data.for && (
          <Question id="adherent">
            <QuestionAdherent
              type={state.data.for}
              value={state.data.adherent}
              onSelect={(
                adherents: IAssuranceSanteInitialState["data"]["adherent"]
              ) => {
                dispatch(
                  updateData({
                    data: { item: "adherent", value: adherents },
                    currentStep: { id: "npa" },
                    currentVisibleStep: { id: "npa" },
                  })
                );
              }}
            />
          </Question>
        )}

        {state.data.adherent.length > 0 && (
          <Question id="npa">
            <QuestionLabel
              title={t("AssuranceSante.npa.key1")}
              description={
                <span>
                  {t("AssuranceSante.npa.key2")}{" "}
                  <button
                    onClick={() =>
                      dispatch(
                        updateData({
                          data: {
                            item: "npa",
                            value: "Not on the territory",
                          },
                          currentStep: { id: "situation" },
                          currentVisibleStep: {
                            id: "situation",
                          },
                        })
                      )
                    }
                    className="font-semibold text-secondary hover:font-bold"
                  >
                    {t("AssuranceSante.npa.key3")}
                  </button>
                </span>
              }
            />
{/*            <QuestionCustomSelect
              placeholder={t("AssuranceSante.npa.key4")}
              options={npaOptions}
              value={state.data.npa ?? ""}
              onSelectValue={(value) => {
                dispatch(
                  updateData({
                    data: { item: "npa", value },
                    currentStep: {
                      id:
                        value === "Not on the territory"
                          ? "situation"
                          : "assurance-actuelle",
                    },
                    currentVisibleStep: {
                      id:
                        value === "Not on the territory"
                          ? "situation"
                          : "assurance-actuelle",
                    },
                  })
                );

                if (state.currentStep.group === "adherent") wizard.nextStep();
              }}
            />*/}

            <QuestionAddressInput
                placeholder={t("AssuranceSante.npa.key4")}
                value={state.data.npa ?? ""}
                onSelectAddress={onSelectAddress}
                searchInputIcon="search"
                showMap={true}
            />
          </Question>
        )}

        {state.data.npa && (
          <React.Fragment>
            {state.data.npa === "Not on the territory" ? (
              <React.Fragment>
                <Question id="situation">
                  <QuestionLabel title={t("AssuranceSante.situation.key1")} />
                  <QuestionSingleRadio
                    inline
                    options={[
                      {
                        label: t("AssuranceSante.situation.key2"),
                        value: "frontalier",
                        appendIcon: (
                          <svg
                            data-name="Emg Frontalier"
                            xmlns="http://www.w3.org/2000/svg"
                            height="2em"
                            width="2em"
                            viewBox="0 0 500 500"
                          >
                            <path d="M281.21 127.33a48.4 48.4 0 1 0-62.42 0 66 66 0 0 0-57.51 65.31v77.05a25 25 0 0 0 3 10.86h-5.38a6 6 0 0 0-6 6v20h-21.34a15.8 15.8 0 0 0-15.78 15.79v75.58a15.81 15.81 0 0 0 15.78 15.79h66.72v15.45A28.85 28.85 0 0 0 250 446.75a28.85 28.85 0 0 0 51.72-17.6V292.48a39.72 39.72 0 0 0 37-39.57v-60.27a66 66 0 0 0-57.51-65.31ZM213.6 90.38a36.4 36.4 0 1 1 36.4 36.4 36.43 36.43 0 0 1-36.4-36.4Zm-48.7 202.2h42v14h-42Zm-37.12 105.33v-75.58a3.79 3.79 0 0 1 3.78-3.79h108.65a3.79 3.79 0 0 1 3.79 3.79v75.58a3.79 3.79 0 0 1-3.79 3.79H131.56a3.79 3.79 0 0 1-3.78-3.79ZM227.14 446a16.87 16.87 0 0 1-16.86-16.86V413.7h29.93a15.8 15.8 0 0 0 3.79-.48v15.93A16.87 16.87 0 0 1 227.14 446Zm99.58-193.1a27.71 27.71 0 0 1-25 27.54v-88.63a6 6 0 0 0-12 0v237.34a16.86 16.86 0 0 1-33.72 0V322.33a15.8 15.8 0 0 0-15.79-15.79h-21.33v-20a6 6 0 0 0-6-6h-5.75a25 25 0 0 0 3.15-12.52v-76.21a6 6 0 0 0-12 0v76.25c0 9.64-7.83 12.4-12.45 12.51-5.52.14-12.09-2.78-12.52-11.45v-76.48a53.92 53.92 0 0 1 53.86-53.86h45.72a53.92 53.92 0 0 1 53.86 53.86Z" />
                          </svg>
                        ),
                      },
                      {
                        label: t("AssuranceSante.situation.key3"),
                        value: "future resident",
                        appendIcon: (
                          <svg
                            data-name="Emg Résident"
                            xmlns="http://www.w3.org/2000/svg"
                            height="2em"
                            width="2em"
                            viewBox="0 0 500 500"
                          >
                            <path d="M463.65 430h-27.79a18.34 18.34 0 0 0 1.14-6.31v-71.2a24.16 24.16 0 0 0 19.92-23.76v-32.46a38.29 38.29 0 0 0-28.19-36.88A28.81 28.81 0 0 0 412.38 213v-23l48.27 27.83a6 6 0 1 0 6-10.4l-54.27-31.29V64a6 6 0 0 0-6-6h-73.26a6 6 0 0 0-6 6v62.9L253 84.2a6 6 0 0 0-6 0L90.63 174.36l-57.27 33a6 6 0 1 0 6 10.4L87.62 190v240H36.35a6 6 0 0 0 0 12h427.3a6 6 0 0 0 0-12Zm-18.73-133.73v32.46A12.15 12.15 0 0 1 437 340.1v-44.27a6 6 0 1 0-12 0v127.82a6.31 6.31 0 0 1-12.62 0v-62a6 6 0 0 0-12 0v62a6.32 6.32 0 0 1-12.63 0V295.83a6 6 0 1 0-12 0v44.27a12.15 12.15 0 0 1-7.92-11.37v-32.46A26.26 26.26 0 0 1 394.06 270h24.63a26.26 26.26 0 0 1 26.23 26.27Zm-21.71-55.07a16.84 16.84 0 1 1-16.83-16.83 16.85 16.85 0 0 1 16.83 16.83ZM339.12 70h61.26v99.13l-61.26-35.31ZM99.62 183 250 96.32 400.38 183v30A28.81 28.81 0 0 0 384 259.39a38.29 38.29 0 0 0-28.19 36.88v32.46a24.17 24.17 0 0 0 19.92 23.76v71.16a18.11 18.11 0 0 0 1.17 6.35H312V262.68a6 6 0 0 0-6-6H194a6 6 0 0 0-6 6V430H99.62ZM200 430V268.68h100V430Z" />
                          </svg>
                        ),
                      },
                    ]}
                    value={state.data.situation}
                    onSelectOption={(value) => {
                      if (value === "frontalier")
                        dispatch(
                          updateData({
                            data: {
                              item: "situation",
                              value: value,
                            },
                            currentStep: { id: "work-hours" },
                            currentVisibleStep: {
                              id: "work-hours",
                            },
                          })
                        );
                      else {
                        dispatch(
                          updateData({
                            data: {
                              item: "situation",
                              value: value,
                            },
                            currentStep: {
                              id: "franchise",
                              group: "besoins",
                              branch: "suisse",
                              newTab: true,
                            },
                            currentVisibleStep: {
                              id: "franchise",
                              group: "besoins",
                              branch: "suisse",
                              newTab: true,
                            },
                          })
                        );
                      }

                      if (state.currentStep.group === "adherent")
                        wizard.nextStep();
                    }}
                  />
                </Question>

                {state.data.situation &&
                  state.data.situation === "frontalier" && (
                    <React.Fragment>
                      <Question
                        id="work-hours"
                        className="flex w-full flex-col gap-10"
                      >
                        {/* Adherent work and insurance infos */}

                        {/* Partner works in swiss */}
                        {state.data.adherent.map((adher, index) => (
                          <React.Fragment key={index}>
                            {adher.type === "partner" && (
                              <Question>
                                <QuestionLabel
                                  title={t("AssuranceSante.work-hours.key1")}
                                />
                                <QuestionSingleRadio
                                  options={[
                                    {
                                      label: t(
                                        "AssuranceSante.work-hours.key2"
                                      ),
                                      value: true,
                                    },
                                    {
                                      label: t(
                                        "AssuranceSante.work-hours.key3"
                                      ),
                                      value: false,
                                    },
                                  ]}
                                  value={adher.travailSuisse}
                                  onSelectOption={(value) => {
                                    const clone = state.data.adherent.map(
                                      (adher) => {
                                        if (adher.type === "partner")
                                          return {
                                            ...adher,
                                            travailSuisse: value,
                                          };
                                        else return adher;
                                      }
                                    );

                                    dispatch(
                                      updateData({
                                        data: {
                                          item: "adherent",
                                          value: clone,
                                        },
                                      })
                                    );
                                  }}
                                />
                              </Question>
                            )}
                          </React.Fragment>
                        ))}

                        {/* Partner is insured by employer */}
                        {state.data.adherent.find(
                          (adhr) => adhr.type === "partner"
                        )?.travailSuisse && (
                          <Question>
                            <QuestionLabel
                              title={t("AssuranceSante.work-hours.key4")}
                            />
                            <QuestionSingleRadio
                              options={[
                                {
                                  label: t("AssuranceSante.work-hours.key2"),
                                  value: true,
                                },
                                {
                                  label: t("AssuranceSante.work-hours.key3"),
                                  value: false,
                                },
                              ]}
                              value={
                                Object.hasOwn(
                                  state.data.adherent.find(
                                    (adhr) => adhr.type === "partner"
                                  ) as object,
                                  "couverture"
                                )
                                  ? !state.data.adherent.find(
                                      (adhr) => adhr.type === "partner"
                                    )?.couverture
                                  : ""
                              }
                              onSelectOption={(value) => {
                                const clone = state.data.adherent.map(
                                  (adher) => {
                                    if (adher.type === "partner")
                                      return {
                                        ...adher,
                                        couverture: value ? false : true,
                                      };
                                    else return adher;
                                  }
                                );

                                dispatch(
                                  updateData({
                                    data: {
                                      item: "adherent",
                                      value: clone,
                                    },
                                  })
                                );
                              }}
                            />
                          </Question>
                        )}

                        {/* Main is insured by employer */}
                        <Question>
                          <QuestionLabel
                            title={t("AssuranceSante.work-hours.key5")}
                          />
                          <QuestionSingleRadio
                            options={[
                              {
                                label: t("AssuranceSante.work-hours.key2"),
                                value: true,
                              },
                              {
                                label: t("AssuranceSante.work-hours.key3"),
                                value: false,
                              },
                            ]}
                            value={
                              Object.hasOwn(
                                state.data.adherent.find(
                                  (adhr) => adhr.type === "main"
                                ) as object,
                                "couverture"
                              )
                                ? !state.data.adherent.find(
                                    (adhr) => adhr.type === "main"
                                  )?.couverture
                                : ""
                            }
                            onSelectOption={(value) => {
                              const clone = state.data.adherent.map((adher) => {
                                if (adher.type === "main")
                                  return {
                                    ...adher,
                                    couverture: value ? false : true,
                                    travailSuisse: true,
                                  };
                                else return adher;
                              });

                              dispatch(
                                updateData({
                                  data: {
                                    item: "adherent",
                                    value: clone,
                                  },
                                })
                              );
                            }}
                          />
                        </Question>

                        {/* Childrens grater than 18 years old */}
                        {state.data.adherent
                          .filter(
                            (adhr) =>
                              adhr.type === "children" &&
                              isOlderThan(adhr.dob, 18)
                          )
                          .map((adhr, index) => (
                            <React.Fragment key={index}>
                              {/* Check if child works in swiss */}
                              <Question>
                                <QuestionLabel
                                  title={t("AssuranceSante.work-hours.key6", {
                                    year: adhr.year,
                                  })}
                                />
                                <QuestionSingleRadio
                                  options={[
                                    {
                                      label: t(
                                        "AssuranceSante.work-hours.key2"
                                      ),
                                      value: true,
                                    },
                                    {
                                      label: t(
                                        "AssuranceSante.work-hours.key3"
                                      ),
                                      value: false,
                                    },
                                  ]}
                                  value={adhr.travailSuisse}
                                  onSelectOption={(value) => {
                                    const nonChildrens =
                                      state.data.adherent.filter(
                                        (adhr) => adhr.type !== "children"
                                      );

                                    const cloneChildrens =
                                      state.data.adherent.filter(
                                        (adhr) =>
                                          adhr.type === "children" &&
                                          isOlderThan(adhr.dob, 18)
                                      );

                                    const updatedChildrens = cloneChildrens.map(
                                      (child, i) => {
                                        if (i === index) {
                                          return {
                                            ...child,
                                            travailSuisse: value as boolean,
                                          }; // Spread existing properties and update travailSuisse
                                        }
                                        return child;
                                      }
                                    );

                                    dispatch(
                                      updateData({
                                        data: {
                                          item: "adherent",
                                          value: [
                                            ...nonChildrens,
                                            ...updatedChildrens,
                                          ],
                                        },
                                      })
                                    );
                                  }}
                                />
                              </Question>
                            </React.Fragment>
                          ))}

                        {/* Confirm changes */}
                        {canConfirm() && (
                          <div className="ml-auto">
                            <button
                              onClick={() => {
                                dispatch(
                                  updateCurrentVisibleStep({
                                    id: "loader",
                                    group: "finalisation",
                                  })
                                );

                                if (state.currentStep.group === "besoins")
                                  wizard.nextStep();
                              }}
                              className="inline-flex items-center justify-center whitespace-nowrap rounded-[56px] text-base font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-center bg-thirdy text-white hover:bg-thirdy/90 h-[56px] px-[26px] mt-4"
                            >
                              {t("AssuranceSante.work-hours.key7")}
                            </button>
                          </div>
                        )}
                      </Question>
                    </React.Fragment>
                  )}
              </React.Fragment>
            ) : (
              <Question id="assurance-actuelle">
                <QuestionLabel
                  title={t("AssuranceSante.assurance-actuelle.key1")}
                />
                <QuestionSingleRadio
                  inline
                  options={[
                    {
                      label: t("AssuranceSante.assurance-actuelle.key2"),
                      value: true,
                    },
                    {
                      label: t("AssuranceSante.assurance-actuelle.key3"),
                      value: false,
                    },
                  ]}
                  value={state.data.hasInsurance}
                  onSelectOption={(value) => {
                    dispatch(
                      updateData({
                        data: {
                          item: "hasInsurance",
                          value: value,
                        },
                        currentStep: !value
                          ? {
                              id: "franchise",
                              group: "besoins",
                              branch: "suisse",
                              newTab: true,
                            }
                          : undefined,
                        currentVisibleStep: !value
                          ? {
                              id: "franchise",
                              group: "besoins",
                              branch: "suisse",
                              newTab: true,
                            }
                          : undefined,
                      })
                    );
                  }}
                />

                {state.data.hasInsurance && (
                  <div className="w-full mt-4">
                    <label className="mb-[6px] block text-sm font-medium opacity-80">
                      {t("AssuranceSante.assurance-actuelle.key4")}
                    </label>
                    <QuestionCustomSelect
                      options={[
                        "Agrisano",
                        "Aquilana",
                        "Assura",
                        "Atupri",
                        "Autre",
                        "Concordia",
                        "CSS",
                        "EGK",
                        "Groupe Mutuel",
                        "Helsana",
                        "KPT",
                        "ÖKK Landquart",
                        "Sanitas",
                        "Sympany",
                        "Visana",
                      ].map((item) => ({ label: item, value: item }))}
                      value={state.data.actualAssurance}
                      onSelectValue={(value) => {
                        dispatch(
                          updateData({
                            data: {
                              item: "actualAssurance",
                              value: value,
                            },
                            currentStep: {
                              id: "franchise",
                              group: "besoins",
                              branch: "suisse",
                              newTab: true,
                            },
                            currentVisibleStep: {
                              id: "franchise",
                              group: "besoins",
                              branch: "suisse",
                              newTab: true,
                            },
                          })
                        );
                      }}
                    />
                  </div>
                )}
              </Question>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );

  return <StepContainer>{content}</StepContainer>;
};

export default AssuranceSante;
